<template>
    <div :class="['checkout', config.platform, mode]">
        <cart-nav
            :options="[ $t('cart_nav.choose_plan'), $t('cart_nav.confirm_details'), $t('cart_nav.complete') ]"
            :active="cartIndex"></cart-nav>
        <div class="checkout-container">
            <div :class="['checkout-container-inner', loading ? 'reo-processing' : '']" v-if="initialized">
                <div class="cart-main" v-if="targetPlanIds">
                    <h1 v-if="config.platform === 'web'">
                        {{ title }}
                    </h1>
                    <addressInput
                        v-if="(needPay || error) && freeOrderArgs === undefined"
                        :addressDetails="addressDetails"
                        @addressChange="handleAddressChange"
                        :defaultExpandAddress="expandAddress"
                        :supportedCountryList="supportedCountryList"
                        :userAddresses="userAddresses"
                        :reolinkAddresses="reolinkAddresses"
                        :VATenabled="VATenabled"
                        @vatEnabledChange="val=>VATenabled=val"
                        :countryError="countryError"
                        :stateError="stateError"
                        :selectedCountry="selectedCountry"
                        @updateCountry="updateCountry"
                        :selectedState="selectedState"
                        @updateStates="updateStates"
                        :vatDetails="vatDetails"
                        @vatChange="handleVatChange"
                        @vatError="(key,error)=>vatDetails[key].error = error"
                        @disableCountryError="countryError=false"
                        @disableStateError="stateError=false"
                        @setDefault="value=>setDefaultAddress=value"
                        @refreshAddress="getAddressOptions"
                        @updateWatchFlag="value=>addressInputWatchFlag = value"
                        @updateLoading="value=>loading=value"
                        @disableAddressError="key=>addressDetails[key].error=false"
                        @disableVatError="key=>vatDetails[key].error = false"
                        @vatValid="value=>vatValid=value"
                        :defaultAddress="defaultAddress"
                        :isCountrySelectDisabled="isCountrySelectDisabled"
                    />

                    <h3 class="sub-title" v-if="isPriceDifference">
                        {{ $t('cart.price_difference.title') }}
                        <reo-tip>
                            {{ $t('cart.price_difference.tip') }}
                        </reo-tip>
                    </h3>
                    <div v-else-if="showSwitchInfo && !isSim" class="switch-title" >
                        <h3 class="sub-title">{{ detailTitle }}</h3>
                        <p v-html="$t('cart.switch_note_1')"></p>
                        <p>{{$t('cart.switch_note_3')}}</p>
                    </div>
                    <h3 v-else class="sub-title">{{ detailTitle }}</h3>
                    <rich-text
                        v-if="countryText && isFirstCharge"
                        class="country-text"
                        :html="countryText"
                        :checkout-apply-promotion-context="true"
                        :apply-promotion-applied="countryApplyPromotionApplied"
                        @click="handleCountryRichTextClick"
                    />
                    <div class="cards">
                        <PlanSubscribeCard
                            v-for="(item,index) in checkoutItems"
                            :key ="item.simManager?.device?.uid"
                            :plan="item.plan"
                            :loading="loading"
                            :startedAt="item.startedAt"
                            :endingAt="item.endingAt"
                            :min="item.min"
                            :max="item.max"
                            :adjustmentInfo="item.adjustmentInfo"
                            :needPay="needPay"
                            :simCard="item.simManager?.data.simCard"
                            :showDelete="checkoutItems.length > 1"
                            :subscribeMode="mode"
                            :lockDeviceData="item.simManager?.lockedDevice"
                            :showAutoRenew="shouldShowItemAutoRenew(item)"
                            :autoRenew="item.autoRenew"
                            :promotions="promotions"
                            :order-item="previewData.items[index]"
                            :apply-promotion-usage-limit-to-qty="applyPromotionUsageLimitFromQuery"
                            :checkout-apply-promotion-context="true"
                            :hide-device-assign="hideDeviceAssign"
                            v-model="item.qty"
                            @delete="deleteCheckoutItem(index)"
                            @selectDevice="()=>selectDevice(index)"
                            @update:autoRenew="value=>updateCheckoutItemAutoRenew(index, value)"
                        />
                    </div>
                    <total-box
                        :previewData="previewData"
                        showCoupon
                        v-show="!isMobile"
                        @addCoupon="handleAddCoupon"
                    />
                    <div v-if="isMobile && subTotal !== 0" class="coupon-input-box">
                        <div class="coupon-input-box-inner">
                            <input
                                type="text"
                                v-model="couponCode"
                                :disabled="inputError === false"
                                @input="inputError = null"
                                :class="['code-input', {'error': inputError === true, 'success': inputError === false}]"
                                :placeholder="$t('cart.coupon.newPlaceholder').toString()"
                            />
                            <reo-common-button
                                :label="inputError === false ? $t('cart.coupon.remove') : $t('cart.coupon.apply')"
                                colorType="dark"
                                class="confirm-button"
                                buttonClass="confirm-button-inner"
                                @click="handleAddCoupon(couponCode, inputError === false ? 'remove' : 'add')"
                            />
                        </div>
                        <div class="message" :class="{'error': inputError === true, 'success': inputError === false}" v-if="inputError !== null">{{ message }}</div>
                    </div>
                    <div class="switch-note" v-if="showSwitchInfo">
                        <h3 class="yellow">{{ $t('cart.switch.title') }}</h3>
                        <ul>
                            <li v-for="(item, index) in (isSim?$t('cart.switch.sim-list'):$t('cart.switch.list'))" :key="index" v-html="item"></li>
                            <li v-if="showSwitchAddOnNote">{{ $t('cart.switch.add_on_note') }}</li>
                        </ul>
                        <p class="description"
                            v-html="$t('cart.switch.help', lang, {
                                link: config.support_email
                            })"></p>
                        <div class="switch-agree-wrap">
                            <reo-old-checkbox v-model="switchAgreement" size="small" :text="$tc('cart.switch.ok')"></reo-old-checkbox>
                            <span class="red">*</span>
                        </div>
                    </div>
                    <div v-if="showRefundNote || hasTrafficLimitNotice" class="refund-note">
                        <span v-if="showRefundNote" v-html="$t('cart.refund_note')"></span>
                        <LimitNoticeDialogTrigger
                            :class="{ 'refund-note__details--standalone': !showRefundNote }"
                            :checkoutItems="checkoutItems"
                            showTrigger
                            v-if="!free"
                        />
                    </div>
                    <hr v-if="!free"/>
                    <div :class="['agreement', mode]">
                        <reo-old-checkbox v-model="agreement" size="small" class="checkbox-left"></reo-old-checkbox>
                        <p
                            v-html="$t('cart.agreement', {
                                conditions: getOfficialUrl( config.conditions ),
                                privacy: getOfficialUrl( config.privacy )
                            })"></p>
                    </div>
                    <checkoutButtons
                        v-if="!isMobile"
                        :mode="mode"
                        :needPay = "needPay"
                        :showCancelButton="showCancelButton"
                        :showSwitchInfo="showSwitchInfo"
                        :disabled="subscripting || error"
                        :isSim="isSim"
                        @click="checkoutConfirm"
                    />
                    <cart-total
                        v-if="isMobile"
                        @subscribe="checkoutConfirm"
                        @addCoupon="handleAddCoupon"
                        :loading="loading"
                        :button="checkoutButton"
                        :disabled="subscripting"
                        :taxIncluded="taxIncluded"
                        :taxData="taxData"
                        :currency="currency"
                        :totalPrice="totalPrice"
                        :sub-total="subTotal"
                        :taxTotal="taxTotal"
                        :shouldExcludeVAT="shouldExcludeVAT"
                        :totalRegularPrice="totalRegularPrice"
                        :VATenabled="VATenabled"
                        :planCards="checkoutItems.map(item=>{
                            return {
                                plan: item.plan,
                                simCard: item.simManager?.data.simCard,
                                startedAt: item.startedAt,
                                endingAt: item.endingAt,
                                quantity: item.qty,
                                lockDeviceData: item.simManager?.lockedDevice,
                                autoRenew: item.autoRenew,
                                showAutoRenew: shouldShowItemAutoRenew(item)
                            }
                        })"
                        :adjustmentInfo="adjustmentInfo"
                        :mode="mode"
                        :showCancelButton="showCancelButton"
                        :cancelButton="cancelButton"
                        :needPay="needPay"
                        :simCard="simCardIccId"
                        :promotion="previewData?.payment?.promotion ?? []"
                        :previewData="previewData"
                    />
                    <p class="switch-statement yellow" v-if="showSwitchInfo && !isSim">{{ $t('cart.switch_note_2') }}</p>
                </div>
                <!-- empty cart -->
                <div class="empty-cart" v-else>
                    <i class="icon-icon_cart-2-101"></i>
                    <h4>{{ $t( 'cart.empty.title' ) }}</h4>
                    <router-link class="ui-button primary-button" to="/">{{ $t( 'cart.empty.button' ) }}</router-link>
                </div>
            </div>
            <div class="bottom-button-container" v-if="bottomButtonShow && !isMobile && initialized">
                <checkoutButtons
                    :mode="mode"
                    :needPay = "needPay"
                    :showSwitchInfo="showSwitchInfo"
                    :disabled="subscripting || error"
                    :showCancelButton="showCancelButton"
                    :isSim="isSim"
                    @click="checkoutConfirm"
                />
            </div>
        </div>
        <InputPaypal
            v-model="showPaypalDialog"
            @confirmInputPaypal="confirmInputPaypal"
        />
        <reo-dialog
            class='selectProduct'
            :title="selectDeviceDialogTitle"
            :deviceTip="mode!=='create'?$t('selectProduct.deviceTip'):''"
            :supportTip="$t('selectProduct.note')"
            :labelOfConfirm="$t('dialog.save')"
            :showButton="!selectProductEmpty"
            isBottomWhenMobile
            use-action-footer
            v-if='selectDeviceItem && showSelectDevice'
            @cancel='showSelectDevice=false'
            @confirm='confirmSelect'
        >
            <template v-slot:attention>
                <div class="attention" v-if="!selectProductEmpty && selectDeviceItem.changeAssociated">
                    <p>{{$t('selectProduct.changeAssociated')}}</p>
                </div>
            </template>
            <select-product
                tableLoc="select"
                @selectedDevicesChange='val=>selectDeviceItem?.updateTempSelectedDevices(val)'
                :deviceData='deviceData'
                :selectedDevices='selectDeviceItem.selectedDevices'
                :selectLimit="selectDeviceItem.planVideoQuantity"
                :subId="selectDeviceItem.prevSub?.id"
                :availableModel="selectDeviceItem.availableModels"
                sticky-header
                @emptyStateChange="selectProductEmpty = $event">
            </select-product>
        </reo-dialog>

        <reo-dialog
            v-if="showOtherRegionDialog"
            @cancel="handleOtherRegionCancel"
        >
            <div class="dialog-content">
                {{ $t('subscribe.not_available_common') }}
            </div>
        </reo-dialog>
    </div>
</template>

<script lang="ts">
import {
    createUserAddress,
    getIPlan,
    getOrderDetailById,
    getPaymentHistory, getReolinkAddresses,
    getSupportedCountries,
    getUserAddresses,
    subscriptions,
    updateUserAddress
} from '@/api/cart';
import { getDeviceList } from '@/api/device';
import { getPromotionItems, previewFreeOrder, previewOrder } from '@/api/order';
import { getPaymentsMethods } from '@/api/payment';
import { getCountryList } from '@/api/s3Config';
import { getSimCardList } from '@/api/sim';
import { getIccidByUid, getSubDeviceList, getSubscriptionById, getSubscriptions } from '@/api/subscriptions';
import reoOldCheckbox from '@/basic/Checkbox.vue';
import ReoDialog from '@/basic/Dialog.vue';
import reolinkGa from '@/common/js/gtagPageview';
import { unfoldError } from '@/common/js/notice';
import { CREATE_ADDRESS_KEYS, debounce, getQueryParam, isInVisibleArea, isNumeric, splitArrayIntoChunks, unitTransfer, valueWithFallbacks } from '@/common/js/utils';
import cartNav from '@/components/cartNav.vue';
import cartTotal from '@/components/cartTotal.vue';
import InputPaypal from '@/components/InputPaypal.vue';
import PlanSubscribeCard from '@/components/PlanSubscribeCard.vue';
import TotalBox from '@/components/TotalBox/index.vue';
import paymentMethods from '@/config/paymentMethods';
import { currStage as currentStage, deliver as deliverStatus, plan as planStatus } from '@/config/status';
import { productsManager } from '@/lib';
import { SimManager } from '@/lib/simManager';
import { uidHandler } from '@/lib/uidHandler';
import { getClampedPromotionUsageLimitQty, getCountryPromotionText, getCountryWithoutOther, getPlanPromotionUsageLimitToItems, handleGetUserDataError, pendingBindDevicesStorage, shouldCheckAgreementByDefault } from '@/utils/business';
import { PlanDisabledError, SubscriptionProcessingError } from '@/utils/error';
import { fetchAllPages } from '@/utils/request';
import { EAccountDeviceStatus, EDeviceInfoModule, ESeriesCategory, ESimCardModule, ESubscribeMode, IAdjustmentInfo, IBillingInfo, ICountryItem, IEsim, IItemPreview, IOrderPreview, IOrderPreviewArgsItem, IPaymentMethod, IPlan, IPreviewFreeOffer_BodyArgs, IProductSeries, IPromotionItem, IReolinkAddresses, ISubscription, ISupportCountry, ITaxDetails, ITotalDevice, IUserAddresses, TAddress, TCreateUserAddressArg } from '@reolink-cloud/interface';
import { TypeAssert } from '@reolink-web/utils';
import { AxiosError } from 'axios';
import { config } from 'CONFIG';
import nprogress from 'nprogress';
import Vue from 'vue';
import { apmMonitor } from '@/service/apmMonitor';
import { EApmTransactionName, EApmTransactionType } from '@/config/apm';
import addressInput from './components/addressInput.vue';
import checkoutButtons from './components/checkoutButtons.vue';
import LimitNoticeDialogTrigger from './components/LimitNoticeDialogTrigger.vue';
import CheckoutItem, { IDeviceInfoFull, ISubscriptionWithPlan, TSelectedDevice } from './lib/checkoutItem';
import { deviceManager } from './lib/deviceManager';
import { BeforeCountryChangeEvent, countryChangeEventBus } from './lib/eventBus';
import { getCheckoutButtonText } from './lib/utils';
import ReoTip from '@/basic/Tip.vue';

const DEBOUNCE_DELAY = 100; //经修改已不需要延迟太久

class AddressInvalidError extends Error {

    public constructor( message: string ) {

        super( message ); // (1)
        this.name = 'AddressInvalidError'; // (2)

    }

}

export default Vue.extend({

    components: {
        reoOldCheckbox,
        cartTotal,
        cartNav,
        ReoDialog,
        addressInput,
        PlanSubscribeCard,
        TotalBox,
        checkoutButtons,
        InputPaypal,
        LimitNoticeDialogTrigger,
        ReoTip
    },
    async created (): Promise<void> {

        await this.getNecessaryDataForInit();
        await this.initData();
        this.getSelectedDevices();

        // 监听国家切换事件
        countryChangeEventBus.addEventListener( 'before-country-change', this.handleCountryChangeValidation as EventListener );

    },
    mounted() {

        this.calcButtonIsInWindow();
        window.addEventListener( 'scroll', this.calcButtonIsInWindow );
        window.addEventListener( 'resize', this.calcButtonIsInWindow );

    },
    destroyed () {

        window.removeEventListener( 'scroll', this.calcButtonIsInWindow );
        window.removeEventListener( 'resize', this.calcButtonIsInWindow );

        // 移除国家切换事件监听
        countryChangeEventBus.removeEventListener( 'before-country-change', this.handleCountryChangeValidation as EventListener );

    },
    data () {

        return {
            // 有 pending_bind_device 时隐藏 Devices Assigned 行，设备绑定由 app 侧处理
            hideDeviceAssign: ( pendingBindDevicesStorage.getItem( 'pendingBindDevices' )?.length ?? 0 ) > 0,
            targetPlanIds: getQueryParam( this.$route, 'p_id' ),
            prevSubIds: getQueryParam( this.$route, 'ps_id', '0' ),
            totalPrice: '0',
            currency: '',
            /**
             * checkout时需要同意的条款
             */
            agreement: false,
            loading: true,
            cartIndex: 1,
            /**
             * 支付方式对应的图标
             */
            paymentMethods,
            /**
             * 预览的套餐起始时间
             */
            startedAt: 0,
            endingAt: 0,
            /**
             * 切换时需要同意的条款
             */
            switchAgreement: false,
            /**
             * 正在订阅
             */
            subscripting: false,
            mode: ESubscribeMode.CREATE as ESubscribeMode,
            config,
            initialized: false,
            // 由使用超过了180天的付费套餐转换到免费套餐时退款需要输入paypal账号
            showPaypalDialog: false,
            dialogConfirmShow: false,
            payerAccount: '',
            payoneerCard: '',
            payoneerName: '',
            refundMethod: '',
            showTips: false,
            expandAddress: true,
            selectedCountry: '',
            selectedState: '',
            cookiesAddress: null as null | Record<string, any>,
            renderingCookieAddress: true,
            addressDetails: {

                postalCode: {
                    value: '',
                    placeholder: this.$t( 'cart.address.postalCode' ),
                    required: true,
                    half: true,
                    autocomplete: 'postal-code',
                    error: false as boolean | string
                },
                firstName: {
                    value: '',
                    placeholder: this.$t( 'cart.address.firstName' ),
                    required: true,
                    half: true,
                    autocomplete: 'given-name',
                    error: false as boolean | string
                },
                lastName: {
                    value: '',
                    placeholder: this.$t( 'cart.address.lastName' ),
                    required: true,
                    half: true,
                    autocomplete: 'family-name',
                    error: false as boolean | string
                },
                line1: {
                    value: '',
                    placeholder: this.$t( 'cart.address.line1' ),
                    required: true,
                    half: false,
                    autocomplete: 'address-line1',
                    error: false as boolean | string
                },
                line2: {
                    value: '',
                    placeholder: this.$t( 'cart.address.line2' ),
                    required: false,
                    half: false,
                    autocomplete: 'address-line2',
                    error: false as boolean | string
                },
                city: {
                    value: '',
                    placeholder: this.$t( 'cart.address.city' ),
                    required: true,
                    half: false,
                    autocomplete: 'address-level2',
                    error: false as boolean | string
                }
            },
            vatDetails: {
                vatNumber: {
                    value: '',
                    placeholder: 'VAT',
                    required: false,
                    half: false,
                    autocomplete: 'vat-number',
                    error: false as boolean | string
                },
                vatCompany: {
                    value: '',
                    placeholder: 'Company Name',
                    required: false,
                    half: false,
                    error: false as boolean | string
                }
            },
            paypalDetails: {
                payerAccount: {
                    value: '',
                    placeholder: this.$t( 'cancel_plan.paypal_account' ),
                    required: true,
                    half: false,
                    autocomplete: 'paypal-account',
                    error: false as boolean | string
                }
            },
            payoneerDetails: {
                payoneerName: {
                    value: '',
                    placeholder: this.$t( 'cancel_plan.payoneer_name' ),
                    required: true,
                    half: false,
                    autocomplete: 'payoneer-name',
                    error: false as boolean | string
                },
                payoneerCard: {
                    value: '',
                    placeholder: this.$t( 'cancel_plan.payoneer_card' ),
                    required: true,
                    half: false,
                    autocomplete: 'payoneer-cardNum',
                    error: false as boolean | string
                }
            },
            tax: { data: [] } as Record<string, any> | null,
            taxData: [] as ITaxDetails[],
            taxTotal: 0,
            countryError: false,
            stateError: false,
            supportedCountryList: [] as ISupportCountry[],
            error: false,
            supportedTrafficDeviceList: '' as string,
            canBuy: false,
            sendAddPaymentInfo: false,
            gtagData: {} as Record<string, any>,
            previewData: {} as IOrderPreview,
            userAddresses: null as IUserAddresses | null,
            reolinkAddresses: null as IReolinkAddresses | null,
            setDefaultAddress: false,
            bottomButtonShow: false,
            privateWatchFlag: false,
            addressInputWatchFlag: true,
            defaultAddress: null as 'billing' | 'shipping' | null,
            showAutoRenewInfo: false,
            vatValid: false,
            checkoutItems: [] as CheckoutItem[],
            showSelectDevice: true,
            selectProductEmpty: false,
            selectDeviceItemIndex: -1,
            deviceManager,
            VATenabled: false,
            subNew: parseInt( this.$route.query.subNew as string ),
            isCountrySelectDisabled: false,
            couponCode: getQueryParam( this.$route, 'coupon', '' )[0],
            promotions: [] as IPromotionItem[],
            deviceData: [] as ITotalDevice[],
            showOtherRegionDialog: this.$route.query.unsupported === 'true',
            paymentMethodList: [] as IPaymentMethod[],
            inputError: null as boolean | null,
            message: '',
            esimProfile: [] as IEsim[]
        };

    },
    methods: {
        handleCountryChangeValidation( event: Event ): void {

            const customEvent = event as BeforeCountryChangeEvent;
            const { targetCountry } = customEvent.detail;

            // 阻止默认的国家切换行为
            event.preventDefault();

            // 构建跳转路径
            const redirectUrl = this.buildRedirectUrl( targetCountry );
            customEvent.detail.redirectUrl = redirectUrl;

        },
        buildRedirectUrl( targetCountry: string ): string {

            // 根据不同套餐类型构建跳转 URL
            const queryParams = new URLSearchParams();
            queryParams.set( 'country', targetCountry );
            const isSwitch = this.mode === ESubscribeMode.SWITCH;
            const setIfHasValue = ( key: string ): void => {

                const value = this.$route.query[key];
                if ( value !== null && value !== undefined && value !== '' ) {

                    queryParams.set( key, String( value ));

                }

            };
            setIfHasValue( 'ps_id' );
            setIfHasValue( 'ymh' );
            const currentPlanType = this.firstCheckoutItem?.planType;
            if ( currentPlanType === ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN ) {

                queryParams.set( 'isAddOn', 'true' );

            }

            if ( currentPlanType === ESeriesCategory.CLOUD_STORAGE_PLAN ) {

                if ( !config.countryData.cloudStorageCountries.includes( targetCountry )) {

                    return location.href + '&unsupported=true';

                }

                if ( isSwitch ) {

                    return  `/user/subscribe-plan/update-cloud?${queryParams.toString()}`;

                }
                return `/cloud-plan`;

            } else if ( this.firstCheckoutItem?.simManager?.lockedDevice ) {

                if ( currentPlanType === ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN ) {

                    return  `/user/subscribe-plan/sim/choose?${queryParams.toString()}`;

                }

                return  `/user/subscribe-plan/lock/choose?${queryParams.toString()}`;

            } else {

                if ( !config.countryData.cloudDataCountries.includes( targetCountry )) {

                    return location.href + '&unsupported=true';

                }
                return  `/user/subscribe-plan/sim/choose?${queryParams.toString()}`;

            }

        },
        async getNecessaryDataForInit (): Promise<void> {

            try {

                await productsManager.update({ lang: this.lang });

            } catch ( err ) {

                console.error( err );
                unfoldError( 'errors.server_error' );

            }

        },
        async initData () {

            this.queryPreCheck();
            try {

                const subscriptions = ( await getSubscriptions()).items;
                [this.esimProfile] = await Promise.all( [
                    getIccidByUid(),
                    deviceManager.init( subscriptions )
                ] );
                this.deviceData = deviceManager.deviceData.filter( device => {

                    return !this.esimProfile.some( esim => esim.uid === device.uid ) && device.boundStatus === 'bound';

                });
                const checkoutItems = await this.getCheckoutItems( subscriptions );
                this.checkoutItems = checkoutItems;
                this.setMode();
                this.initDataFromCheckoutItems( checkoutItems );
                this.setGaData( checkoutItems );
                await this.getAddress();
                this.setDefaultAgreement();
                const [ , , paymentMethods ] = await Promise.all( [
                    this.initPreview(), this.getPromotions(), getPaymentsMethods()
                ] );

                this.paymentMethodList = paymentMethods.items;

            } catch ( e ) {

                console.error( e );
                this.loading = false;
                this.error = true;
                if ( e instanceof PlanDisabledError ) {

                    unfoldError( 'errors.plan_not_available', 9999999 );
                    setTimeout(() => {

                        this.$router.push( e.redirectArgs ).catch( console.error );

                    }, 1000 );

                } else if ( e instanceof SubscriptionProcessingError ) {

                    unfoldError( 'errors.sub_processing', 9999999 );

                } else {

                    handleGetUserDataError( e, this.$router );

                }

            }

            this.initialized = true;
            nprogress.done();

        },
        async getAddress (): Promise<void> {

            let orders = await getPaymentHistory( 'all', 1, null, 1 );
            let historyPayments: any[] = [];
            for ( let order of orders.items ) {

                // * 获取上一个订单的地址，无论是否完成
                historyPayments.push( await getOrderDetailById( order.id ));
                break;

            }
            // 有缓存地址则直接渲染显示，否则显示表单

            let vatTax = {
                customerNumber: ''
            };
            if ( historyPayments.length !== 0 ) {

                let latestPayment = historyPayments[0].payer;
                if ( latestPayment.country !== '' && this.supportedCountryList.some( item => item.code === latestPayment.country )) {

                    this.cookiesAddress = latestPayment;

                }
                for ( let tax of historyPayments[0].taxData.details ) {

                    if ( tax.customerNumber !== '' ) {

                        vatTax = tax;
                        if ( this.cookiesAddress )
                            this.cookiesAddress.vatTax = tax;
                        break;

                    }

                }

            }

            if ( this.free ) {

                return;

            }
            if ( this.isSupportedCountry( this.country )) {

                this.selectedCountry = this.country;
                this.isCountrySelectDisabled = true;

            }
            if ( this.cookiesAddress && this.isSupportedCountry( this.cookiesAddress.country ) && ( !this.isCountrySelectDisabled || this.cookiesAddress.country === this.selectedCountry )) {

                this.addressDetails.firstName.value = this.cookiesAddress.name.split( ' ' )[0];
                this.addressDetails.lastName.value = this.cookiesAddress.name.split( ' ' )[1];
                this.selectedCountry = this.cookiesAddress.country;
                this.selectedState = this.cookiesAddress.state;
                this.addressDetails.city.value = this.cookiesAddress.city;
                this.addressDetails.line1.value = this.cookiesAddress.line1;
                this.addressDetails.line2.value = this.cookiesAddress.line2;
                this.addressDetails.postalCode.value = this.cookiesAddress.postalCode;
                this.vatDetails.vatCompany.value = typeof this.cookiesAddress.company === 'undefined' ? '' : this.cookiesAddress.company;
                if ( JSON.stringify( vatTax ) !== '' ) {

                    this.vatDetails.vatNumber.value = vatTax.customerNumber;

                }
                this.selectedState = this.cookiesAddress.state;
                this.expandAddress = false;

            } else {

                this.expandAddress = true;

            }

        },
        queryPreCheck() {

            let preCheckErrorKey = '';
            if ( !this.targetPlanIds || this.targetPlanIds.length === 0 ) {

                preCheckErrorKey = 'errors.no_plan_selected';

            }
            if ( preCheckErrorKey !== '' ) {

                unfoldError( preCheckErrorKey, 100000 );
                this.loading = false;
                return false;

            }

        },
        setMode() {

            let mode = this.$route.query['mode'] as ESubscribeMode;
            if ( !mode && this.firstCheckoutItem ) {

                const checkoutItem = this.firstCheckoutItem;
                if ( checkoutItem.prevSub?.plan === checkoutItem.plan.id ) {

                    mode = ESubscribeMode.RENEW;

                } else if ( !checkoutItem.prevSub ) {

                    mode = ESubscribeMode.CREATE;

                } else {

                    mode = ESubscribeMode.SWITCH;

                }

            }
            this.mode = mode;
            if ( mode === 'create' ) {

                this.$store.commit( 'CREATINGPLAN', true );

            } else {

                this.$store.commit( 'CREATINGPLAN', false );

            }
            for ( const item of this.checkoutItems ) {

                item.setMode( mode );

            }

        },
        async getCheckoutItems( subscriptions: ISubscription[] ) {

            const allCountryList = await getCountryList();
            const simManagers = await this.getSimManagers();
            const planCache = {
                cache: new Map<string, IPlan>(),
                async get( planId: string ) {

                    const plan = this.cache.get( planId );
                    return plan ?? await getIPlan( planId );

                }
            };
            const checkoutItems: CheckoutItem[] = [];
            for ( const [index, planId] of this.targetPlanIds.entries()) {

                let plan = await planCache.get( planId );
                plan = this.getPlanSupportedCountries( plan, allCountryList );
                if ( plan.status === planStatus.DISABLED ) {

                    const planType = plan.series?.category;
                    let path = '';
                    const mode = this.$route.query['mode'] === 'renew' ? ESubscribeMode.SWITCH : this.mode;
                    if ( simManagers[0]?.data.esimDevice ) {

                        path = '/user/subscribe-plan/lock/choose';

                    } else if ( [ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN, ESeriesCategory.CLOUD_DATA_PLAN].includes( planType! )) {

                        path = '/user/subscribe-plan/sim/choose';

                    } else {

                        path = mode === ESubscribeMode.SWITCH ? '/user/subscribe-plan/update-cloud' : '/cloud-plan';

                    }
                    throw new PlanDisabledError({
                        path,
                        query: {
                            ...this.$route.query,
                            mode
                        }
                    });

                }
                let prevSub: ISubscriptionWithPlan| undefined;
                const prevSubId = this.prevSubIds[index];
                if ( !['', '0', undefined, null].includes( prevSubId )) {

                    const correspondSub = subscriptions.find( item => item.id === prevSubId );
                    if ( correspondSub ) {

                        prevSub = correspondSub;

                    } else {

                        prevSub = await getSubscriptionById( prevSubId );

                    }
                    prevSub.planInfo = await planCache.get( prevSub.plan );

                }
                // // 兼容旧版本app
                if ( prevSubId === '0' && !this.subNew && plan.series?.category === ESeriesCategory.CLOUD_STORAGE_PLAN  ) {

                    const activeSubs = subscriptions.filter( sub => sub.status === 'active' );
                    const inactiveSubs = subscriptions.filter( sub => sub.status !== 'active' );
                    if ( activeSubs.length !== 0 ) {

                        let minIdSub = activeSubs[0];
                        for ( let sub of activeSubs ) {

                            if ( sub.id < minIdSub.id ) {

                                minIdSub = sub;

                            }

                        }
                        prevSub = minIdSub;

                    } else {

                        inactiveSubs.sort(( a, b ) => b.expiredAt - a.expiredAt );
                        prevSub = inactiveSubs[0];

                    }
                    prevSub.planInfo = await planCache.get( prevSub.plan );

                }
                if ( prevSub && ( prevSub.status[currentStage.DELIVER] === deliverStatus.PROCESSING || prevSub.status[currentStage.DELIVER] === deliverStatus.PENDING )) {

                    throw new SubscriptionProcessingError();

                }
                const simManager = simManagers[index];
                checkoutItems.push( new CheckoutItem( plan, simManager, prevSub, this.mode ));

            }

            return checkoutItems;

        },
        initDataFromCheckoutItems( checkoutItems: CheckoutItem[] ) {

            this.currency = checkoutItems[0].plan.pricing.currency;
            for ( const item of checkoutItems ) {

                item.autoRenew = this.getDefaultAutoRenew( item );

            }
            this.supportedCountryList = ( checkoutItems[0].plan.series as IProductSeries ).supportedCountryList;
            for ( const country of this.supportedCountryList ) {

                country.code = country.code.toUpperCase();

            }

        },
        setGaData( checkoutItems: CheckoutItem[] ) {

            this.gtagData = {
                currency: this.currency,
                items: checkoutItems.map( item => {

                    const plan = item.plan;
                    let name = ( plan.series?.i18n.name ?? plan.id );
                    const itemListName = `(cloud-Plan)storageRegion:${this.selectedCountry};`;
                    name += `(${plan.duration.amount}-${plan.duration.unit})`;
                    name += `(${plan.style['cloud_storage.video_lifetime'] ?? 0}-days,${plan.style['cloud_storage.max_device_quantity'] ?? 0}-cams,${unitTransfer( plan.style['cloud_storage.space_capacity'] ?? 0 )}${unitTransfer( plan.style['cloud_storage.space_capacity'] ?? 0, 'unit' )})`;
                    return {
                        item_id: plan.id,
                        item_name: `(cloud-Plan)${name}`,
                        item_category: 'cloud-Plan',
                        price: Number( plan.pricing.salePrice ),
                        item_list_name: itemListName,
                        item_variant: `mode:${this.mode};`,
                        quantity: item.qty
                    };

                })
            };
            for ( const item of checkoutItems ) {

                // 更新当前的统计状态
                reolinkGa.subscribeMode = this.mode;
                reolinkGa.prevPlan = item.prevSub?.planInfo?.id ?? '';
                reolinkGa.nextPlan = item.plan.id;
                reolinkGa.sendSubscribeMode();

            }
            if ( !this.needPay ) {

                this.gtagData.value = 0.00;
                reolinkGa.sendECommerceEvent( 'add_payment_info', this.gtagData );

            }

        },
        async checkoutConfirm (): Promise<void> {

            // Record checkout confirmation event in APM (transaction-specific)
            apmMonitor.addTransactionLog({
                event: 'checkout_confirm_clicked',
                itemsCount: this.checkoutItems.length,
                subscribeMode: this.mode
            });

            reolinkGa.sendSimpleEvent( `${this.currentPlanTypeSuffer}-Checkout` );
            if ( this.showSwitchInfo && !this.switchAgreement ) {

                // 如果是切换套餐，须同意切换条款
                unfoldError( 'errors.checkbox_required' );
                // TODO document.querySelector('.switch-note').getBoundingClientRect().top - 200,
                window.scrollTo( 0, $( '.switch-note' ).offset()!.top - 200 );
                return;

            } else if ( !this.agreement ) {

                // 须同意支付条款
                unfoldError( 'errors.checkbox_required' );
                window.scrollTo( 0, $( '.agreement' ).offset()!.top - 400 );
                return;

            } else if ( this.needPay && !this.checkFormValid()) {

                // 地址表单*项必填
                if ( !this.expandAddress ) {

                    this.expandAddress = true;

                }
                if ( this.selectedCountry === '' ) {

                    this.countryError = true;

                }
                if ( this.selectedState === '' ) {

                    this.stateError = true;

                }
                for ( let key in this.addressDetails ) {

                    let item = this.addressDetails[key];
                    if ( item.required && ( !item.value || item.value === '' )) {

                        item.error = true;

                    }

                }
                unfoldError( 'errors.field_required' );
                window.scrollTo( 0, $( '.checkout-container-inner' ).offset()!.top );
                return;

            } else if ( this.needPay && this.selectedCountry === 'US' ) {

                // 校验美国邮编格式
                let exec = /^([0-9]{5})(-[0-9]{4})?$/;
                if ( !exec.test( this.addressDetails.postalCode.value )) {

                    if ( !this.expandAddress ) {

                        this.expandAddress = true;

                    }
                    unfoldError( 'errors.invalid_postalCode' );
                    this.addressDetails.postalCode.error = true;
                    window.scrollTo( 0, $( '.checkout-container-inner' ).offset()!.top );
                    return;

                }

            }
            if ( isNumeric( this.addressDetails.city.value )) {

                //city是数字的话会导致后面支付报错

                unfoldError( 'errors.cityIsNumber' );
                this.addressDetails.city.error = true;
                window.scrollTo( 0, $( '.checkout-container-inner' ).offset()!.top );
                return;

            }

            if ( !this.checkAddressLengthValid()) {

                return;

            }
            await this._subscriptions();

        },
        async preview ( previewItems: IOrderPreviewArgsItem[], init?: boolean ): Promise<{
            couponError: string;
            hasCouponError: boolean;
        }> {

            // Start transaction for order preview operation
            apmMonitor.changeTransactionName( EApmTransactionName.LEVEL1_ORDER_PREVIEW );

            this.loading = true;
            const country = this.countryContext;

            try {

                const noShippingInfo = !this.checkAddressLengthValid() || this.free || !this.finalAddressValid;
                const data = await ( this.freeOrderArgs ? previewFreeOrder( this.freeOrderArgs ) : previewOrder({
                    items: previewItems,
                    shippingInfo: noShippingInfo ? undefined : this.finalAddress,
                    context: { lang: this.lang, country },
                    promotionType: this.$route.query['promotionType'] as string,
                    couponCode: this.couponCode ? [this.couponCode] : undefined
                }));
                this.previewData = data;

                let { payment, items } = data;
                for ( const [index, currentOrderItem] of items.entries()) {

                    const currentCheckoutItem = this.checkoutItems[index];
                    currentCheckoutItem.qty = currentCheckoutItem.lastQty = currentOrderItem.quantity;
                    if ( init && currentCheckoutItem.min === 0 && currentOrderItem.quantity === 1 ) {

                        currentCheckoutItem.min = 1;

                    }
                    currentCheckoutItem.startedAt = currentOrderItem.context.effectivity.startedAt;
                    currentCheckoutItem.endingAt = currentOrderItem.context.effectivity.endingAt;
                    if ( currentOrderItem?.context.adjustment ) {

                        currentCheckoutItem.adjustmentInfo = { currency: this.previewData.currency, amount: parseFloat( currentOrderItem.subTotal ) };

                    }

                }
                this.totalPrice = Number( parseFloat( payment?.amount.value ?? '0' )).toFixed( 2 );
                this.taxData = data.payment?.tax.details ?? [];

                // Update APM context with cart value (transaction-specific)
                apmMonitor.addTransactionLog(
                    { event: 'cart_value_updated', cartValue: parseFloat( this.totalPrice ), currency: data.currency }
                );

                this.loading = false;
                this.error = false;

                // 检查优惠券是否失败
                let couponError = '';
                let hasCouponError = false;
                if ( data.payment?.failedPromotions && data.payment?.failedPromotions.length > 0 ) {

                    const errorReason = data.payment.failedPromotions[0].reason;

                    if ( this.$i18n.te( `cart.errors.${errorReason}` )) {

                        const error = this.$t( `cart.errors.${errorReason}`, {
                            coupon: this.couponCode
                        }).toString();

                        unfoldError( error );
                        couponError = error;

                    } else {

                        unfoldError( 'cart.errors.order_common_error_message' );
                        couponError = this.$t( 'cart.errors.order_common_error_message' ).toString();

                    }
                    if ( data.payment.failedPromotions.some( item => item.couponCode === this.couponCode )) {

                        this.couponCode = '';
                        hasCouponError = true;

                    }

                }

                apmMonitor.addTransactionLog({ event: 'order_preview_success', cartValue: parseFloat( this.totalPrice ) });

                return {
                    couponError,
                    hasCouponError
                };

            } catch ( err ) {

                console.error( err );
                this.loading = false;
                this.error = true;
                this.privateWatchFlag = true;

                for ( const item of this.checkoutItems ) {

                    item.resetQty();

                }

                let symbol = ( err as AxiosError ).response?.data.error.symbol;
                let key = 'errors.error_unknown';
                if ( symbol === 'violate_subscribe_restriction' ) {

                    let reason = ( err as AxiosError ).response?.data.error.metadata.reason;
                    if ( reason === 'switchFromOther' ) {

                        key = 'errors.can_not_switch_from_other';

                    } else if ( reason === 'onlyOneFreePlan' ) {

                        key = 'errors.one_free_plan';

                    } else if ( reason === 'subscriptionMaxPurchaseCycles' ) {

                        key = 'errors.violate_cycle_limitation';

                    } else if ( reason === 'userMaxSubscribeTimes' || reason === 'userMaxSubscriptionQty' ) {

                        key = 'errors.violate_sub_limitation';

                    }

                }
                if ( !TypeAssert.isUndefined( this.freeOrderArgs )) {

                    key = 'errors.server_error';

                }
                unfoldError( key, 999999 );

                apmMonitor.captureError( err as Error );
                apmMonitor.addTransactionLog({ event: 'order_preview_failed', error: key });

                return {
                    couponError: '',
                    hasCouponError: false
                };

            } finally {

                apmMonitor.endTransaction(  );

            }

        },
        checkAddressLengthValid(): boolean {

            const returnFalse = (): boolean => {

                this.expandAddress = true;
                return false;

            };
            for ( let key in this.addressDetails ) {

                if ( key === 'lastName' ) {

                    let totalName = this.addressDetails.lastName.value + ' ' + this.addressDetails.firstName.value;
                    if ( totalName.length > 30 ) {

                        this.addressDetails.lastName.error = this.$tc( 'name too long' );
                        this.addressDetails.firstName.error = this.$tc( 'name too long' );
                        unfoldError( 'errors.name_length_error', 5000 );
                        window.scrollTo( 0, $( '.checkout-container-inner' ).offset()?.top ?? 0 );
                        return returnFalse();

                    }

                }
                if ( key === 'line1' ) {

                    if ( this.addressDetails.line1.value.length > 80 ) {

                        this.addressDetails.line1.error = this.$tc( 'address too long' );
                        unfoldError( 'errors.address1_length_error', 5000 );
                        window.scrollTo( 0, $( '.checkout-container-inner' ).offset()?.top ?? 0 );
                        return returnFalse();

                    }

                }
                if ( key === 'line2' ) {

                    if ( this.addressDetails.line2.value.length > 80 ) {

                        this.addressDetails.line2.error = this.$tc( 'address too long' );
                        unfoldError( 'errors.address2_length_error', 5000 );
                        window.scrollTo( 0, $( '.checkout-container-inner' ).offset()?.top ?? 0 );
                        return returnFalse();

                    }

                }
                if ( key === 'postalCode' ) {

                    if ( this.addressDetails.postalCode.value.length > 60 ) {

                        this.addressDetails.postalCode.error = this.$tc( 'postalCode too long' );
                        unfoldError( 'errors.postalCode_length_error', 5000 );
                        window.scrollTo( 0, $( '.checkout-container-inner' ).offset()?.top ?? 0 );
                        return returnFalse();

                    }

                }
                if ( key === 'city' ) {

                    if ( this.addressDetails.city.value.length > 50 ) {

                        this.addressDetails.city.error = this.$tc( 'city too long' );
                        unfoldError( 'errors.city_length_error', 5000 );
                        window.scrollTo( 0, $( '.checkout-container-inner' ).offset()?.top ?? 0 );
                        return returnFalse();

                    }

                }

            }
            if ( this.selectedState.length > 30 ) {

                this.stateError = true;
                unfoldError( 'errors.state_length_error', 5000 );
                window.scrollTo( 0, $( '.checkout-container-inner' ).offset()?.top ?? 0 );
                return returnFalse();

            }
            if ( this.VATenabled ) {

                for ( let key in this.vatDetails ) {

                    const maxLength = key === 'vatCompany' ? 150 : 50;

                    if ( this.vatDetails[key].value.length > maxLength ) {

                        this.vatDetails[key].error = this.$tc( `${key} too long` );
                        unfoldError( `errors.${key}_length_error`, 5000 );
                        window.scrollTo( 0, $( '.checkout-container-inner' ).offset()?.top ?? 0 );
                        return returnFalse();

                    }

                }

            }

            return true;

        },
        /**
         * 第一次preview，确定初始的数量和min、max
         */
        async initPreview (): Promise<void> {

            const previewItems = this.checkoutItems.map( item => item.getPreviewOrderItem( this.mode, this.lang ));
            await this.preview( previewItems );
            this.privateWatchFlag = true;

        },
        async _subscriptions (): Promise<void> {

            // Start transaction for payment submission
            apmMonitor.changeTransactionName( EApmTransactionName.LEVEL1_ORDER_CREATE );

            this.subscripting = true;

            if ( this.subscripting ) {

                try {

                    let shippingAddress = this.finalAddress;
                    const orderItems = this.checkoutItems.map(
                        item =>
                            item.getCreateOrderItem(
                                this.mode,
                                this.lang,
                                this.shouldShowItemAutoRenew( item ) ? item.autoRenew : undefined
                            )
                    );

                    this.gtagData.value = this.previewData.payment?.amount;
                    reolinkGa.sendECommerceEvent( 'view_cart', this.gtagData );
                    reolinkGa.sendECommerceEvent( 'begin_checkout', this.gtagData );
                    let manualRefundInfo;
                    if ( this.refundMethod === 'Paypal' ) {

                        manualRefundInfo = {

                            method: 'paypal',
                            account: this.paypalDetails.payerAccount.value

                        };

                    } else if ( this.refundMethod === 'Payoneer' ) {

                        manualRefundInfo = {

                            method: 'payoneer',
                            number: this.payoneerDetails.payoneerCard.value,
                            holderName: this.payoneerDetails.payoneerName.value

                        };

                    } else {

                        manualRefundInfo = {};

                    }

                    // Record payment method selection (transaction-specific)
                    const paymentMethod = this.refundMethod || 'default';
                    apmMonitor.addTransactionLog(
                        { event: 'payment_method_selected', paymentMethod, country: this.countryContext }
                    );

                    await this.saveAsDefaultAddress();
                    const country = this.countryContext;
                    const currency = this.previewData.currency;
                    const amount = this.previewData.payment?.amount.value ?? '0.00';
                    await subscriptions(
                        orderItems,
                        this.needPay,
                        this.mode,
                        manualRefundInfo,
                        shippingAddress,
                        currency,
                        amount,
                        country,
                        this.$router,
                        this.freeOrderArgs,
                        this.$route.query['promotionType'] as string,
                        this.couponCode
                    );

                    apmMonitor.addTransactionLog({ event: 'payment_submit_success' });
                    apmMonitor.endTransaction();

                } catch ( err ) {

                    if ( err instanceof AddressInvalidError ) {

                        this.subscripting = false;
                        apmMonitor.addTransactionLog({ event: 'payment_submit_failed', error: 'invalid_address' });
                        apmMonitor.endTransaction();
                        return;

                    }

                    let symbol = ( err as AxiosError ).response?.data.error.symbol;
                    const reason = ( err as AxiosError ).response?.data?.error?.metadata?.reason;
                    let key = '';
                    this.showPaypalDialog = false;
                    this.error = true;

                    if ( symbol === 'violate_subscribe_restriction' ) {

                        this.loading = false;
                        let reason = ( err as AxiosError ).response?.data.error?.metadata?.reason;
                        if ( reason === 'switchFromOther' ) {

                            key = 'errors.can_not_switch_from_other';

                        } else if ( reason === 'subscriptionMaxPurchaseCycles' ) {

                            key = 'errors.violate_cycle_limitation';

                        } else if ( reason === 'userMaxSubscribeTimes' || reason === 'userMaxSubscriptionQty' ) {

                            key = 'errors.violate_sub_limitation';

                        }  else if ( reason === 'exceedMaxAddOnPackageQty' ) {

                            key = 'errors.exceedMaxAddOnPackageQty';

                        }
                        else if ( reason === 'onlyOneFreePlan' ) {

                            key = 'errors.one_free_plan';

                        } else if ( reason === 'simCard is not associatable' ) {

                            key = 'errors.simCardNotassociatable';

                        } else if ( reason === 'incomplete refund task found' || reason === 'service is refunding' ) {

                            key = 'errors.incompleteRefund';

                        } else if ( reason === 'simMaxSubscribeCycles' ) {

                            key = 'errors.simMaxSubscribeCycles';

                        } else if ( reason === 'subscription is refunding' ) {

                            key = 'errors.service_refunding';

                        }
                        else {

                            key = 'errors.violate_subscribe_restriction';

                        }

                    } else if ( symbol === 'tax_expired' || symbol === 'tax_invalid' ) {

                        // tax_report_expired
                        key = 'errors.tax_report_expired';
                        this.showPaypalDialog = false;
                        await this.calculateTax();

                    } else if ( symbol === 'payer_account_required' ) {

                        this.showPaypalDialog = true;
                        // if ( config.oem !== 'reolink' ) {

                        this.refundMethod = 'Paypal';

                        key = 'errors.payer_account_required';

                        this.error = false;

                        // } else {

                        //     this.$nextTick(() => {

                        //         (this.$refs.paypalDialog as any).disabledBtn();

                        //     });

                        // }

                    } else if ( symbol === 'malformed_order' ) {

                        const reason = ( err as AxiosError ).response?.data.error?.metadata?.reason;
                        switch ( reason ) {

                            case 'device is already creating subscription':

                                key = 'errors.device_already_creating';
                                break;
                            case 'order total amount mismatch':

                                key = 'cart.errors.order_common_error_message';
                                break;

                            default:

                                key = 'errors.plan_not_available';

                        }

                    } else if ( symbol === 'invalid_device_found' ) {

                        key = 'errors.invalid_device_found';

                    } else {

                        key = 'errors.server_error';

                    }
                    //处理新引入的error symbol
                    switch ( symbol ) {

                        case 'cannot_transfer_to_free_plan':
                            key = 'errors.cannot_transfer_to_free_plan';
                            this.waitAndNavigate(() => {

                                this.$router.back();

                            });
                            break;
                        case 'parent_subscription_should_be_active':
                            key = 'errors.parent_subscription_should_be_active';
                            this.waitAndNavigate( async () => {

                                await this.$router.push({ path: '/user/my-sim', query: {
                                    ymh: uidHandler.encrypt( this.firstCheckoutItem?.simManager?.iccid ?? '' )
                                } });

                            });
                            break;
                        case 'incorrect_postal_code':
                            key = 'errors.invalid_postalCode';
                            this.addressDetails.postalCode.error = true;
                            window.scrollTo( 0, $( '.checkout-container-inner' ).offset()!.top );
                            break;
                        case 'usr_locked':
                            key = 'errors.usr_locked';
                            if ( reason === 'user locked for auto-renew' ) {

                                key = 'errors.user_locked_for_auto-renew';

                            }
                            break;
                        case 'exceed_max_device_num':
                            key = 'errors.exceed_max_device_num';
                            break;
                        case 'upgrade_maintenance':
                            key = 'errors.service_maintenance';
                            break;
                        case 'invalid_promotion_qualification':
                            key = 'cart.errors.fresh_page_and_try_again';
                            break;
                        case 'order_operation_frequent':
                            key = 'cart.errors.fresh_page_and_try_again';
                            break;

                    }
                    if ( !TypeAssert.isUndefined( this.freeOrderArgs )) {

                        key = 'errors.server_error';

                    }
                    unfoldError( key );
                    console.error( err );
                    this.showTips = false;
                    this.subscripting = false;

                    apmMonitor.captureError( err as Error );
                    apmMonitor.addTransactionLog({ event: 'payment_submit_failed', error: key });
                    apmMonitor.endTransaction();

                }

            }

        },
        async calculateTax (): Promise<void> {

            if ( true ) {

                await Promise.resolve();
                this.tax = { data: [] };
                return;

            }

        },
        async saveAsDefaultAddress() {

            if ( this.setDefaultAddress ) {

                try {

                    const address: Record<string, any> = {};
                    for ( const key of CREATE_ADDRESS_KEYS ) {

                        if ( key in this.addressDetails ) {

                            address[key] = this.addressDetails[key].value;

                        } else if ( key === 'country' ) {

                            address[key] = this.selectedCountry;

                        } else if ( key === 'state' ) {

                            address[key] = this.selectedState;

                        }
                        else {

                            address[key] = '';

                        }

                    }
                    if ( this.VATenabled ) {

                        address.vatNumber = this.vatDetails.vatNumber.value;
                        address.company = this.vatDetails.vatCompany.value;

                    }
                    address.isDefault = true;
                    address.type = 'billing';
                    await createUserAddress( address as TCreateUserAddressArg );

                } catch ( e ) {

                    const symbol = ( e as AxiosError ).response?.data.error.symbol;
                    if ( symbol === 'user_address_duplicated' ) {

                        const addressId = ( e as AxiosError ).response?.data.error.metadata.addressId;
                        if ( addressId ) {

                            await updateUserAddress({ id: addressId, isDefault: true });

                        } else {

                            unfoldError( 'cart.address.save_address_fail' );

                        }

                    } else if ( symbol === 'invalid_postal_code' ) {

                        if ( !this.expandAddress ) {

                            this.expandAddress = true;

                        }
                        unfoldError( 'errors.invalid_postalCode' );
                        this.addressDetails.postalCode.error = true;
                        window.scrollTo( 0, $( '.checkout-container-inner' ).offset()!.top );
                        throw new AddressInvalidError( 'invalid_postal_code' );

                    } else if ( symbol === 'invalid_user_name' ) {

                        this.addressDetails.firstName.error = true;
                        this.addressDetails.lastName.error = true;
                        unfoldError( `errors.invalid_user_name`, 5000 );
                        window.scrollTo( 0, $( '.checkout-container-inner' ).offset()!.top );
                        throw new AddressInvalidError( 'invalid_user_name' );

                    } else {

                        unfoldError( 'cart.address.save_address_fail' );

                    }

                }

            }

        },
        checkFormValid (): boolean {

            // 地址表单*项必填
            if ( this.selectedCountry === '' || this.selectedState === '' ) {

                return false;

            }
            for ( let key in this.addressDetails ) {

                let item = this.addressDetails[key];
                if ( item.required && ( !item.value || item.value === '' )) {

                    return false;

                }

            }
            return true;

        },
        showRefundFormError ( refundMethod ): boolean {

            const group = refundMethod === 'Payoneer' ? this.payoneerDetails : this.paypalDetails;
            let showError = false;
            for ( let key in group ) {

                let item = group[key];
                if ( item.required ) {

                    if ( !item.value || item.value === '' ) {

                        item.error = this.$t( 'errors.field_required' );
                        showError = true;

                    }
                    if ( item.value.length > 255 ) {

                        item.error = this.$t( 'errors.common_length_error', 'type', { option: item.placeholder }) as string;
                        showError = true;

                    }

                }

            }

            return showError;

        },
        showConfirmDialog (): void {

            if ( this.showPaypalDialog && this.showRefundFormError( this.refundMethod )) {

                return;

            }
            this.dialogConfirmShow = true;
            this.showPaypalDialog = false;

        },
        cancel (): void {

            this.showPaypalDialog = false;
            this.dialogConfirmShow = false;
            this.loading = false;
            this.refundMethod = '';
            this.paypalDetails.payerAccount.value = '';
            this.payoneerDetails.payoneerCard.value = '';
            this.payoneerDetails.payoneerName.value = '';

        },
        async getAddressOptions() {

            try {

                this.userAddresses = await getUserAddresses();
                if ( this.userAddresses.bindReolinkAccount ) {

                    this.reolinkAddresses = await getReolinkAddresses();

                }

            } catch ( err ) {

                console.error( err );
                unfoldError( 'errors.server_error' );

            }

        },

        async getDeviceList(): Promise<IDeviceInfoFull[]> {

            let uids = getQueryParam( this.$route, 'ymh' );
            uids = uids.map( uid => uidHandler.decrypt( uid ));
            const [deviceListRes, subDevices ] = await Promise.all( [ getDeviceList(
                { uids: uids,
                    includes: [EDeviceInfoModule.CHANNELS, EDeviceInfoModule.MODEL, EDeviceInfoModule.CLOUDSERVICES ],
                    statuses: [EAccountDeviceStatus.BOUND],
                    lang: this.lang
                }
            ), getSubDeviceList()] );
            const cloudServiceArr = Object.values( deviceListRes.cloudServices ?? {});
            return uids.map( uid => {

                const deviceInfo = deviceListRes.items.find( device => device.uid === uid );
                const subDeviceInfo = subDevices.find( device => device.uid === uid );
                const activeAddOns = cloudServiceArr.filter( service => {

                    if ( service.type === 'package' && service.status === 'active' ) {

                        const parentService = deviceListRes.cloudServices?.[service.parentId!];
                        if ( parentService?.associations.some( association => 'uid' in association && association.status === 'active' && association.uid === uid )) {

                            return true;

                        }

                    }
                    return false;

                });
                if ( !deviceInfo ) {

                    throw new Error( 'deviceList wrong response' );

                }
                return {
                    deviceInfo,
                    subDeviceInfo,
                    activeAddOns
                };

            });

        },
        async getSimManagers(): Promise<SimManager[]> {

            let iccids = getQueryParam( this.$route, 'ymh' );
            iccids = iccids.map( iccid => uidHandler.decrypt( iccid )).filter( iccid => iccid ).map(
                id => {

                    if ( id.length === 20 ) {

                        return id;

                    } else {

                        return this.esimProfile.find( item => item.uid === id )?.iccid ?? '';

                    }

                }
            );

            const chunks = splitArrayIntoChunks( iccids, 100 );
            const simManagers: SimManager[] = [];
            for ( const chunk of chunks ) {

                const getSimListRes = await getSimCardList({
                    iccids: chunk,
                    includes: [ESimCardModule.SIM_CARD, ESimCardModule.CLOUD_SERVICES, ESimCardModule.LAST_USED_DEVICE],
                    limit: 100, page: 1,
                    sortStrategy: 'simCardList'
                });
                const simCardItems = iccids.map( iccid => {

                    const item = getSimListRes.items.find( item => item.simCard?.iccid === iccid );
                    if ( !item ) {

                        throw new Error( `Get Sim Card ${iccid} fail` );

                    }

                    return item;

                });
                const chunkSimManagers = simCardItems.map( item => new SimManager( item, getSimListRes.services ));
                await Promise.all( chunkSimManagers.map( simManager => simManager.init()));
                simManagers.push( ...chunkSimManagers );

            }

            return simManagers;

        },
        getPlanSupportedCountries( planDetails: IPlan, allCountryList: ICountryItem[] ) {

            let countryGroupNames = planDetails.series?.countryGroups ?? [];
            let planSeriesCountries: string[] = [];
            for ( let countryGroupName of countryGroupNames ) {

                const countryGroup = productsManager.getCountryGroupByKey( countryGroupName );
                if ( typeof countryGroup !== 'undefined' ) {

                    for ( let country of countryGroup.countries ) {

                        planSeriesCountries = planSeriesCountries.concat( country.key );

                    }

                }

            }
            getSupportedCountries( allCountryList, planSeriesCountries, planDetails.series as IProductSeries );

            return planDetails;

        },
        handleAddressChange( key: string, value: any ) {

            if ( key in this.addressDetails ) {

                this.addressDetails[key].value = value;

            } else if ( key === 'all' ) {

                this.addressDetails = value;

            }

        },
        getDefaultAutoRenew( item: CheckoutItem ): boolean {

            return !item.free
                && item.plan.series?.category !== ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN
                && item.getPlanRenewable( this.mode );

        },
        shouldShowItemAutoRenew( item: CheckoutItem ): boolean {

            return this.isSupportedAutoRenewCountry
                && this.needPay
                && item.plan.series?.category !== ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN
                && item.getPlanRenewable( this.mode );

        },
        updateCheckoutItemAutoRenew( index: number, value: boolean ): void {

            const targetItem = this.checkoutItems[index];
            if ( !targetItem ) {

                return;

            }

            targetItem.autoRenew = value;
            reolinkGa.sendSimpleEvent( `${this.currentPlanTypeSuffer}-Auto-renewal` );

        },
        setAddress( address: TAddress ) {

            this.addressDetails.firstName.value = address.firstName;
            this.addressDetails.lastName.value = address.lastName;
            this.selectedCountry = address.country;
            this.selectedState = address.state;
            this.addressDetails.city.value = address.city;
            this.addressDetails.line1.value = address.line1;
            this.addressDetails.line2.value = address.line2;
            this.addressDetails.postalCode.value = address.postalCode;
            this.vatDetails.vatCompany.value = typeof address.company === 'undefined' ? '' : address.company;
            if ( 'vatNumber' in address ) {

                this.vatDetails.vatNumber.value = address.vatNumber ?? '';

            }
            this.expandAddress = false;

        },
        handleVatChange( key: string, value: string ) {

            this.vatDetails[key].value = value;

        },
        updateStates( state: string ) {

            this.selectedState = state;

        },
        calcButtonIsInWindow(): void {

            window.setTimeout(() => {

                let visible = isInVisibleArea( '.cart-button' );
                this.bottomButtonShow = !visible;

            }, 100 );

        },
        waitAndNavigate( action: () => any ) {

            setTimeout(() => {

                if ( this.$route.path.includes( 'checkout' )) {

                    action();

                }

            }, 5000 );

        },
        vatValidWrapper: debounce( async function ( this: any, fn: () => any )
        {

            if ( this.vatValid ) {

                if ( fn ) {

                    fn();

                } else {

                    await this.commonPreview();

                }

            }

        },
        DEBOUNCE_DELAY
        ),
        inputWatch: debounce( async function ( this: any ) {

            if ( this.watchFlag && this.finalAddressValid ) {

                await this.commonPreview();

            }

        }, DEBOUNCE_DELAY ),
        async commonPreview( ): Promise<{
            couponError: string;
            hasCouponError: boolean;
        }> {

            this.privateWatchFlag = false;
            const previewItems = this.checkoutItems.map( item => item.getPreviewOrderItem( this.mode, this.lang ));
            const { couponError, hasCouponError } = await this.preview( previewItems );
            this.privateWatchFlag = true;
            return {
                couponError,
                hasCouponError
            };

        },
        isSupportedCountry( country: string ) {

            return this.supportedCountryList.some( supportedCountry => supportedCountry.code === country || supportedCountry.text === country );

        },
        setDefaultAgreement(): void {

            const targetCountry = valueWithFallbacks( this.selectedCountry, this.$store.getters.country, config.countryData.ipCountry );
            this.agreement = shouldCheckAgreementByDefault( targetCountry );

        },
        updateCountry( country: string ) {

            this.selectedCountry = country;
            this.setDefaultAgreement();

        },
        async deleteCheckoutItem( index: number ) {

            if ( this.checkoutItems.length > 1 ) {

                this.checkoutItems.splice( index, 1 );
                this.calcButtonIsInWindow();
                await this.commonPreview();

            }

        },
        confirmSelect() {

            this.selectDeviceItem?.confirmSelectDevices();
            this.showSelectDevice = false;

        },
        selectDevice( index: number ) {

            this.selectProductEmpty = false;
            this.selectDeviceItemIndex = index;
            this.showSelectDevice = true;

        },
        getSelectedDevices() {

            let rawSelectedDevice = this.$route.query['selected_devices'];
            if ( typeof rawSelectedDevice === 'string' && rawSelectedDevice !== '' ) {

                rawSelectedDevice = decodeURI( rawSelectedDevice );
                const selectedDevices: TSelectedDevice[] = rawSelectedDevice.split( ';' ).map( item => JSON.parse( item ));
                this.checkoutItems[0].selectedDevices = selectedDevices;

            }

        },
        async confirmInputPaypal( account: string ) {

            this.paypalDetails.payerAccount.value = account;
            await this._subscriptions();

        },

        async getPromotions() {

            if ( !this.selectedCountry ) {

                return;

            }
            this.promotions = await fetchAllPages( getPromotionItems, {
                page: 1,
                limit: 100,
                country: this.selectedCountry,
                lang: this.lang,
            });

        },
        handleCountryRichTextClick( _e: MouseEvent, dataAction: string | null ) {

            if ( dataAction !== 'apply-promotion' ) {

                return;

            }
            if ( this.countryApplyPromotionApplied ) {

                return;

            }
            /** 与 PlanSubscribeCard.syncPromotionUsageLimitFromPlan({ force }) 一致，不依赖 router 变化触发子组件 watch */
            for ( const item of this.checkoutItems ) {

                const clamped = getClampedPromotionUsageLimitQty(
                    this.promotions,
                    item.plan,
                    item.min ?? 1,
                    item.max
                );
                if ( clamped != null && clamped !== item.qty ) {

                    item.qty = clamped;

                }

            }
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    apply_promotion_usage_limit: '1'
                } as Record<string, string | string[]>
            }).catch( console.error );

        },
        async handleAddCoupon( code: string, type?: 'add' | 'remove', callback?: ( success: { couponError: string; hasCouponError: boolean; }) => void ) {

            apmMonitor.startOrRenameTransaction( EApmTransactionName.LEVEL1_COUPON_APPLY, EApmTransactionType.USER_INTERACTION );

            let currentCode = code;

            try {

                if ( currentCode === '' && type === 'add' ) {

                    this.inputError = true;
                    this.message = this.$t( 'cart.coupon.required' ).toString();
                    apmMonitor.addTransactionLog({ event: 'coupon_apply_failed', error: 'empty_code' });
                    return;

                }

                if ( type === 'remove' ) {

                    currentCode = '';

                }

                this.couponCode = currentCode;

                // Record coupon code change (transaction-specific)
                if ( currentCode ) {

                    apmMonitor.addTransactionLog(
                        { event: 'coupon_code_set', couponCode: currentCode, operation: type ?? 'add' }
                    );

                }

                const { couponError, hasCouponError } = await this.commonPreview();

                this.inputError = currentCode === '' ? null : hasCouponError;
                this.message = hasCouponError ? couponError : this.$t( 'cart.coupon.success' ).toString();

                if ( callback ) {

                    callback({ couponError, hasCouponError });

                }

                apmMonitor.addTransactionLog({
                    event: hasCouponError ? 'coupon_apply_failed' : 'coupon_apply_success',
                    couponCode: currentCode
                });
                apmMonitor.endTransaction();

            } catch ( err ) {

                apmMonitor.captureError( err as Error );
                apmMonitor.addTransactionLog({ event: 'coupon_apply_failed', error: 'exception' });
                apmMonitor.endTransaction();
                throw err;

            }

        },
        async handleOtherRegionCancel() {

            this.showOtherRegionDialog = false;
            await this.$router.push({ path: '/' });

        }

    },
    computed: {

        currentTaxDetail(): ITaxDetails|undefined {

            return this.taxData[0];

        },
        taxIncluded(): boolean {

            return this.currentTaxDetail?.taxIncluded ?? false;

        },
        shouldExcludeVAT(): boolean {

            // shouldExcludeVAT：true  免税开关已开启 vat号通过 total = payment.amount.value-vatTax excludeTax
            // shouldExcludeVAT：false 不免税 total = payment.amount.value includeTax
            return ( !this.taxData[0]?.exempted ) && ( !this.taxData[0]?.charged );

        },
        totalRegularPrice (): number {

            return parseFloat( this.previewData.regularSubTotal );

        },
        subTotal(): number {

            return  parseFloat( this.previewData.subTotal );

        },
        selectDeviceDialogTitle(): string {

            if ( !this.selectDeviceItem ) {

                return this.$t( 'selectProduct.title' ).toString();

            }

            const hadSelect = this.selectDeviceItem.hadSelect ?? 0;
            const planVideoQuantity = this.selectDeviceItem.planVideoQuantity ?? 0;
            return `${this.$t( 'selectProduct.title' ).toString()} (${hadSelect}/${planVideoQuantity})`;

        },
        title (): string {

            let { mode } = this;
            let prevSub, plan;
            if ( this.checkoutItems.length === 1 ) {

                const firstCheckoutItem = this.checkoutItems[0];
                prevSub = firstCheckoutItem.prevSub;
                plan = firstCheckoutItem.plan;

            } else if ( this.checkoutItems.length > 1 ) {

                return this.$t( 'subscribe.renewAll' ).toString();

            }
            let title = '';
            if ( !mode || !plan ) {

                return '';

            }
            if ( mode === 'switch' && prevSub ) {

                title = prevSub.planInfo.series.i18n.name;

            } else {

                title = plan.title ?? '';

            }
            let finalMode = mode;
            if ( mode === ESubscribeMode.SWITCH && !this.showSwitchInfo ) {

                finalMode = ESubscribeMode.CREATE;

            }

            return mode
                ? ( this.$t( `cart.title.${finalMode}`, { plan: title }) as string )
                : '';

        },
        detailTitle(): string {

            let { mode } = this;
            return mode ? this.$tc( `cart.plan_details_title.${mode}` ) : '';

        },
        checkoutButton(): string {

            return getCheckoutButtonText( this.mode, this.needPay, this.showSwitchInfo );

        },
        cancelButton(): string {

            let { mode } = this;
            return mode ? this.$tc( `cart.cancel.${mode}` ) : '';

        },
        onlyAddOn(): boolean {

            return this.checkoutItems.every( item => item.plan.series?.category === ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN );

        },
        onlyCloudPlan(): boolean {

            return this.checkoutItems.every( item => item.plan.series?.category === ESeriesCategory.CLOUD_STORAGE_PLAN );

        },
        showRefundNote(): boolean {

            return !this.free && this.config.showRefundWarning;

        },
        hasTrafficLimitNotice(): boolean {

            return this.checkoutItems.some( item => {

                return [ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN, ESeriesCategory.CLOUD_STORAGE_DATA_PLAN, ESeriesCategory.CLOUD_DATA_PLAN].includes( item.plan.series!.category );

            });

        },
        client(): string {

            return this.config.oem;

        },
        lang(): string {

            return this.$store.getters.lang;

        },

        currentPlanType(): ESeriesCategory | undefined {

            return this.firstCheckoutItem?.planType;

        },

        currentPlanTypeSuffer(): string {

            return this.currentPlanType === ESeriesCategory.CLOUD_STORAGE_PLAN ? 'Cloud Storage Service' : 'sim list';

        },

        country(): string {

            return getCountryWithoutOther( this.$store.getters.country );

        },

        userId(): string {

            return this.$store.getters.userId;

        },
        isMobile ( ) {

            return  this.$store.getters.isMobile;

        },
        finalAddress(): IBillingInfo {

            const address = {} as IBillingInfo;
            address.firstName = this.addressDetails.firstName.value;
            address.lastName = this.addressDetails.lastName.value;
            address.postalCode = this.addressDetails.postalCode.value;
            address.city = this.addressDetails.city.value;
            address.line1 = this.addressDetails.line1.value;
            address.line2 = this.addressDetails.line2.value;
            address.country = this.selectedCountry;
            address.state = this.selectedState;
            address.company = typeof this.vatDetails.vatCompany.value === 'undefined' ? '' : this.vatDetails.vatCompany.value;
            if ( this.vatDetails.vatNumber.value !== '' ) {

                address.vatNumber = this.vatDetails.vatNumber.value;

            }
            return address;

        },
        finalAddressValid(): boolean {

            const checkList = Object.keys( this.finalAddress ).filter( key => !['line2', 'company'].includes( key ));
            for ( const prop of checkList ) {

                if ( this.finalAddress[prop] === '' ) {

                    return false;

                }

            }
            if ( !this.checkAddressLengthValid()) {

                return false;

            }
            return true;

        },
        currentItemPreview(): IItemPreview|undefined {

            return this.previewData.items?.[0];

        },
        adjustmentInfo(): IAdjustmentInfo|undefined {

            return this.firstCheckoutItem?.adjustmentInfo;

        },
        firstCheckoutItem(): CheckoutItem|undefined {

            return this.checkoutItems[0];

        },
        showSwitchAddOnNote(): boolean {

            return this.firstCheckoutItem?.planType === ESeriesCategory.CLOUD_STORAGE_PLAN  &&
                    this.firstCheckoutItem?.prevSub?.planInfo?.series?.category === ESeriesCategory.CLOUD_STORAGE_DATA_PLAN;

        },
        isPrevSubActive(): boolean {

            return this.firstCheckoutItem?.prevSub !== undefined && ( this.firstCheckoutItem.prevSub.status !== 'inactive' );

        },
        isSupportedAutoRenewCountry(): boolean {

            const targetCountry = valueWithFallbacks( this.selectedCountry, this.$store.getters.country, config.countryData.ipCountry );

            return this.paymentMethodList.some( item => {

                return item.supportPasslessCountries?.includes( targetCountry );

            });

        },

        /**
         * 是否检查地址并在一些项改变时preview
         * 注意addressInput组件也能改变这个行为(选择地址时禁止watch)
         */
        watchFlag(): boolean {

            return this.privateWatchFlag && this.addressInputWatchFlag;

        },

        showSwitchInfo(): boolean {

            return this.mode === ESubscribeMode.SWITCH && this.isPrevSubActive;

        },

        showCancelButton(): boolean {

            return false;

        },
        simCardIccId(): string|undefined {

            return this.previewData?.items[0]?.context.simCard?.iccid;

        },
        needPreview(): boolean {

            return this.checkoutItems.some( item => item.needPreview );

        },
        free(): boolean {

            return this.checkoutItems.every( item => item.free );

        },
        needPay(): boolean {

            return  Number( this.previewData.payment?.amount.value ?? '0' ) > 0;

        },
        selectDeviceItem(): CheckoutItem | undefined {

            return this.checkoutItems[this.selectDeviceItemIndex];

        },
        deviceOverflow(): any {

            return this.$route.query.deviceOverflow;

        },

        deviceGroupChange(): any {

            return this.$route.query.deviceGroupChange;

        },
        isSim(): boolean {

            return this.firstCheckoutItem?.planType !== ESeriesCategory.CLOUD_STORAGE_PLAN;

        },
        freeOrderArgs(): IPreviewFreeOffer_BodyArgs|undefined {

            if ( !TypeAssert.isUndefined( this.$route.query.freeOrder )) {

                return {
                    lang: this.lang,
                    region: this.$route.query.country as string,
                    uid: uidHandler.decrypt( this.$route.query.inDoorbellProgress as string )
                };

            }

            return undefined;

        },
        countryContext(): string {

            return valueWithFallbacks( this.$route.query['country'] as string, this.selectedCountry, this.country );

        },
        countryText(): string | undefined {

            return getCountryPromotionText( this.promotions, this.checkoutItems.map( item => item.plan ));

        },
        isFirstCharge(): boolean {

            return this.previewData.items?.some( item => item.context.isFirstCharge === true ) ?? false;

        },
        /** 来自套餐页营销 RichText 内按钮跳转时携带，用于按促销 usageLimitToItems 预填周期/数量 */
        applyPromotionUsageLimitFromQuery(): boolean {

            return this.$route.query.apply_promotion_usage_limit === '1';

        },
        countryApplyPromotionApplied(): boolean {

            if ( !this.applyPromotionUsageLimitFromQuery ) {

                return false;

            }
            const promotions = this.promotions;
            return this.checkoutItems.every( item => {

                const limit = getPlanPromotionUsageLimitToItems( promotions, item.plan );
                if ( limit == null ) {

                    return true;

                }
                return item.qty === limit;

            });

        },
        isPriceDifference(): boolean {

            return Boolean( this.firstCheckoutItem?.adjustmentInfo?.amount );

        },
    },
    watch: {
        async needPreview( newVal ) {

            if ( !this.initialized ) {

                return false;

            }
            if ( newVal ) {

                await this.commonPreview();

            }

        },
        async selectedCountry( ): Promise<void> {

            if ( !this.watchFlag ) {

                return;

            }

            await this.getPromotions();

            if ( this.finalAddressValid ) {

                await this.commonPreview();

            }

        },
        async selectedState(): Promise<void> {

            if ( !this.watchFlag ) {

                return;

            }

            if ( this.finalAddressValid ) {

                await this.commonPreview();

            }

        },
        refundMethod( newVal ): void {

            if ( newVal === 'Payoneer' ) {

                this.paypalDetails.payerAccount.required = true;
                this.paypalDetails.payerAccount.error = false;

            } else {

                this.payoneerDetails.payoneerCard.required = true;
                this.payoneerDetails.payoneerName.required = true;
                this.payoneerDetails.payoneerCard.error = false;
                this.payoneerDetails.payoneerName.error = false;

            }

            if (( this.$refs.paypalDialog as any )?.confirmDisabled ) {

                ( this.$refs.paypalDialog as any ).enabledBtn();

            }

        },
        async finalAddressValid( newVal ) {

            if ( newVal && this.privateWatchFlag ) {

                for ( const item of Object.values( this.addressDetails )) {

                    item.error = false;

                }
                for ( const item of Object.values( this.vatDetails )) {

                    item.error = false;

                }
                await this.commonPreview();

            }

        },
        'vatDetails.vatNumber.value': {
            async handler() {

                await this.commonPreview();

            }
        },
        'vatDetails.vatCompany.value': {
            async handler() {

                await this.vatValidWrapper();

            }
        },
        'addressDetails.line1.value': {
            async handler() {

                await this.vatValidWrapper();

            }
        },
        'addressDetails.line2.value': {
            async handler() {

                await this.vatValidWrapper();

            }
        },
        'addressDetails.postalCode.value': {
            async handler() {

                await this.inputWatch();

            }
        },
        error( newVal: boolean ) {

            if ( newVal ) {

                this.expandAddress = true;
                this.checkAddressLengthValid();

            }

        }

    },
});
</script>

<style lang="less" scoped>
    body .payment-content {
        .reo-tip {
            position: absolute;
            right: 0;
            top: -8px;
            .content {
                @media @snapeak_mobile {
                    left: calc(-6.9rem + 50%);
                }
            }
        }
    }
    .switch-agree-wrap {
        .checkbox-label {
            color: #333;
        }
        .checkbox-wrap-container .text {
            font-weight: 500;
        }
    }

    .coupon-input-box{
        display: flex;
        flex-direction: column;
        width: 100%;
        margin-top: 2px;
        margin-bottom: 12px;
        .coupon-input-box-inner{
            border-radius: 8px;
            height: 48px;
            display: flex;
            flex-direction: row;
        }
        .code-input{
            padding: 12px;
            box-shadow: none;
            box-sizing: border-box;
            width: 100%;
            border-radius: 8px 0 0 8px;
            border: 1px solid #E1E1E1;
            font-size: 16px;
            line-height: 1.2;
            &:hover {
                border: 1px solid var(--b_6);
            }
            &.error{
                border-color: @red_5;
            }
            &.success{
                cursor: not-allowed;
            }
        }
        .confirm-button{
            /deep/ .button-label{
                white-space: nowrap;
            }
            .confirm-button-inner{
                border-radius: 0 8px 8px 0;
                padding: 13px 15px;
            }
        }
        .message{
            font-size: 12px;
            font-weight: 400;
            text-align: start;
            margin-bottom: 10px;
            margin-top: 4px;
            &.error{
                color: @red_5;
            }
            &.success{
                color: @green_5;
            }
        }
    }

</style>

<style scoped lang="less">
    @border-color: #dbdbdb;
    @quantity-border: #c6c6c6;
    .checkout {
        background-color: @bg_01;
        padding: 0.6rem 0;
        @media @snapeak_mobile {
            padding: 0;
        }

    }
    .checkout.app .checkout-container {
        padding-bottom: 200px;
    }
    .checkout-container {
        max-width: 1200px;
        margin: 0 auto;
        background-color: @bg_02;
        padding: 0.6rem 0;
        border-radius: 12px;
        @media @snapeak_mobile {
            margin: 0;
            padding: 0;
            background-color: @bg_01;
        }
    }
    .checkout-container-inner {
        max-width: 760px;
        margin: auto;
    }
    .cart-main {
        padding-bottom: 1rem;
        position: relative;
        @media @snapeak_mobile {
            padding: 0 15px 0.8rem;
        }
        .cards{
            margin-top: 20px;
            margin-bottom: 96px;
            display: flex;
            flex-direction: column;
            gap: 10px;
            @media @snapeak_mobile{
                margin: 10px 0;
            }
        }
    }
    /deep/ h1 {
        margin-bottom: 37px;
        font-family: @font_01;
        font-size: 40px;
        font-weight: normal;
        line-height: normal;
        text-align: center;
        @media @snapeak_mobile {
            font-size: 30px;
            font-weight: normal;
            margin-bottom: 30px;
        }
    }

    .sub-title {
        font-size: 20px;
        font-weight: bold;
        /deep/ .reo-tip{
            vertical-align: middle;
        }
        @media @snapeak_mobile {
            font-size: 0.36rem;
        }
    }
    .switch-title {
        margin-bottom: 0.4rem;
        .sub-title {
            margin-bottom: 0.05rem;
        }
        p {
            font-size: 0.16rem;
            @media @snapeak_mobile {
                font-size: 0.26rem;
            }
        }
    }

    .plan-cycle-col > div > span {
        vertical-align: middle;
    }
    @media screen and (max-width: 1199px) {
        .quantity {
            input {
                height: 0.52rem;
                font-size: 0.3rem;
                background-color: #eaeaea;
            }
            input[type="button"] {
                box-sizing: border-box;
                width: 0.5rem;
                border: none;
                &.minus {
                    border-radius: 100% 0 0 100%;
                }
                &.plus {
                    border-radius: 0 100% 100% 0;
                }
            }
            input[type="number"] {
                border: none;
                color: #333;
            }
        }
    }
    .auto-renew{
        display: flex;
        align-items: flex-end;
        flex-direction: column;
        gap: 4px;
        margin-top: -40px;
        padding-bottom: 27px;
        @media @snapeak_mobile{
            background-color: @bg_03;
            margin: 10px 0;
            align-items: stretch;
            padding: 20px;
            position: relative;
        }
        p{
            width: 380px;
            text-align: left;
            font-size: 12px;
            line-height: normal;
            font-weight: 500;
            @media @snapeak_mobile{
                width: auto;
                max-width: calc(100% - 38px);
                font-weight: 500;
            }
        }
        p.title-line{
            font-size: 20px;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-top: 1px solid @line;
            padding-top: 20px;
            @media @snapeak_mobile{
                padding: 0;
                border: none;
                font-weight: 600;
                font-size: 16px;
            }
            span{
                display: inline-flex;
                align-items: center;
                gap: 4px;
            }
            .switch{
                @media @snapeak_mobile{
                    position: absolute;
                    right: 20px;
                    top: 50%;
                    transform: translateY(-50%)
                }
            }
            .info_button{
                color: @brand_light;
                font-size: 20px;
                margin-left: 2px;
                @media @mobile{
                    font-size: 16px;
                }
            }
        }
        /deep/ .ui-link{
            text-align: left;
            .text{
                font-size: 12px;
            }
            .ui-icon{
                font-size: 12px;
            }
        }
    }
    hr {
        border: none;
        border-bottom: 1px solid @line;
        margin-bottom: 0.4rem;
        @media @snapeak_mobile {
            display: none;
        }
    }
    .support {
        max-width: 875px;
        margin-bottom: 0.2rem;
    }
    .payment {
        .payment-content {
            border-radius: 8px;
            border: 1px solid @border-color;
            padding: 0.4rem 0 0.4rem 0.3rem;
            font-size: 0;
            font-weight: 500;
            margin-bottom: 0.2rem;
            position: relative;
            box-shadow: 0 0 6px 1px rgba(0, 0, 0, 0.12);
            @media @snapeak_mobile {
                margin-bottom: 0.4rem;
                padding-top: 0.44rem;
                padding-bottom: 0.44rem;
                .bracket {
                    display: none;
                }
            }
            .credit-cart-text {
                display: none;
                @media @snapeak_mobile {
                    display: inline;
                }
            }
            @media @snapeak_mobile {
                .credit-cart {
                    display: block;
                }
            }
            .payment-desc {
                display: inline-block;
                margin-left: 0.2rem;
                vertical-align: middle;
                font-size: 0.16rem;
                @media @snapeak_mobile {
                    font-size: 0.27rem;
                }
            }
            &.active {
                border: 1px solid @theme;
            }
            .payment-main {
                width: 0.6rem;
                vertical-align: middle;
                @media @snapeak_mobile {
                    width: 1.2rem;
                }
            }
            .payment-ways {
                width: 0.3rem;
                vertical-align: middle;
                @media @snapeak_mobile {
                    width: 0.65rem;
                    max-width: 66px;
                }
            }
            .payment-ways + .payment-ways {
                margin-left: 0.1rem;
            }
        }
    }
    .agreement {
        padding-left: 0.3rem;
        margin-bottom: 0.8rem;
        position: relative;
        .checkbox-left {
            position: absolute;
            left: 0;
            top: 0;
        }
        p {
            font-size: 0.16rem;
        }
        @media @snapeak_mobile {
            padding-left: calc(22px + 0.2rem);
            font-size: 0.24rem;
            margin-bottom: 0.6rem;
            p {
                font-size: 0.26rem;
                font-weight: 500;
                line-height: 0.36rem;
            }
        }
    }

    .switch-note {
        background-color: @bg_03;
        padding: 0.4rem 0.6rem;
        margin-bottom: 0.6rem;
        border: 1px solid @line;
        @media @snapeak_mobile {
            padding: 0.6rem 0.3rem;
        }
        h3 {
            font-weight: 700;
            font-size: 0.2rem;
            padding-bottom: 0.2rem;
            margin-bottom: 0.2rem;
            border-bottom: 1px solid @line_light;
            @media @snapeak_mobile {
                font-size: 0.36rem;
                padding-bottom: 0.3rem;
                margin-bottom: 0.3rem;
            }
        }
        h4 {
            font-size: 0.2rem;
            font-weight: 700;
            margin-bottom: 0.15rem;
            @media @snapeak_mobile {
                font-size: 0.36rem;
                margin-bottom: 0.3rem;
            }
        }
        ul {
            padding-left: 0.2rem;
            margin-bottom: 0.15rem;
            li {
                font-size: 0.16rem;
                list-style: disc;
            }
            @media @snapeak_mobile {
                padding-left: 0.4rem;
                margin-bottom: 0.3rem;
                li {
                    font-size: 0.26rem;
                }
                li + li {
                    margin-top: 0.15rem;
                }
            }
        }
        /deep/ .ui-link{
            display: inline-flex;
            align-items: center;
            .text{
                font-size: .16rem;
                @media @mobile{
                    font-size:.26rem;
                }
            }
            .ui-icon{
                font-size: .16rem;
                @media @mobile{
                    font-size:.26rem;
                }
            }
        }
        .more-details {
            margin-bottom: 0.25rem;
            display: inline-block;
            @media @snapeak_mobile {
                margin-bottom: 0.44rem;
            }
        }
        .description {
            border-top: 1px solid @line_light;
            padding-top: 0.2rem;
            margin-bottom: 0.15rem;
            font-size: 0.16rem;
            @media @snapeak_mobile {
                padding-top: 0.44rem;
                font-size: 0.24rem;
                margin-bottom: 0.22rem;
            }
        }
        .switch-agree {
            font-size: 0.18rem;
            font-weight: 500;
            vertical-align: middle;
            display: inline-block;
            margin-left: 0.1rem;
            @media @snapeak_mobile {
                font-size: 0.26rem;
                margin-left: 0.15rem;
            }
        }
        @media @snapeak_mobile {
            .switch-agree-wrap {
                margin-bottom: 0.6rem;
            }
        }
    }
    .switch-statement {
        position: absolute;
        bottom: 0.5rem;
        transform: translateY(50%);
        text-align: center;
        font-size: 0.16rem;
        @media @snapeak_mobile {
            margin-top: 0.6rem;
            position: static;
            transform: none;
            text-align: left;
            font-size: 0.26rem;
        }
    }
    .save-advance {
        font-size: 0.16rem;
        @media @snapeak_mobile {
            font-size: 0.26rem;
        }
    }
    .empty-cart {
        text-align: center;
        padding-bottom: 1rem;
        @media @snapeak_mobile {
            padding: 1rem 15px 2rem;
            .ui-button {
                box-sizing: border-box;
                width: 100%;
            }
        }
        h4 {
            font-size: 0.2rem;
            font-weight: 700;
            margin-bottom: 0.5rem;
            @media @snapeak_mobile {
                font-size: 0.36rem;
                margin-bottom: 1.2rem;
            }
        }
        i {
            font-size: 1.5rem;
            color: #e0e0e0;
            margin-bottom: 0.3rem;
            display: inline-block;
            @media @snapeak_mobile {
                font-size: 2.7rem;
                margin-bottom: 0.6rem;
            }
        }
    }

    .reo-vfx-area {
        display: inline-block;
        width: 100%;
        margin-bottom: 20px;

        &.half-width {
            width: calc((100% - 20px) / 2);

            &:nth-child(even) {
                margin-left: 20px;
            }
        }

        .reo-vfx-box {
            position: relative;
            display: block;
        }

        .reo-vfx-input {
            font-weight: 500;
            font-size: 16px;
            padding: 0 20px;
            width: 100%;
            height: 48px;
            line-height: 48px;
            box-sizing: border-box;
            outline: none;
            border: 1px solid transparent;
            border-radius: 6px;
            box-shadow: 0 0 6px 2px rgba(0,0,0,.14);
            @media @snapeak_mobile {
                font-size: .28rem;
            }

            &:focus {
                background-color: transparent;
                border-color: @brand_light;
            }
            &.error {
                border-color: @error;
            }

        }

        .reo-vfx-placeholder {
            white-space: nowrap;
            transition: all 0.3s ease-in-out;
            position: absolute;
            top: 50%;
            left: 20px;
            transform: translateY(-50%);
            pointer-events: none;
            font-size: 16px;
            font-weight: 500;
            display: inline-block;
            padding: 0 4px;
            color: @font_secondary;
            @media @snapeak_mobile {
                font-size: .28rem;
            }
        }

        .reo-vfx-input:focus ~ .reo-vfx-placeholder,
        .reo-vfx-input:not(.empty) ~ .reo-vfx-placeholder {
            top: 0;
            font-size: 14px;
            color: @brand_lightest;
            background-color: @bg_02;
            @media @snapeak_mobile {
                font-size: .24rem;
            }
        }

        .reo-vfx-input:focus ~ .reo-vfx-placeholder {
            color: @brand_lightest;
        }

       .reo-vfx-required-flag {
            position: absolute;
            top: 0;
            right: -10px;
            color: #e74330;
            font-weight: 500;
            font-size: 18px;
        }

        .input-tip {
            font-size: 14px;
            color: #777;
            font-weight: 500;
            line-height: 1.2;
            margin-top: 16px;
        }
    }
    @media @snapeak_mobile {
        .reo-vfx-area.half-width[name="postalCode"] {
            margin-left: 0;
            width: 100%;
        }
    }

    .how-to-use-traffic {
        text-align: left;
        font-size: 0.18rem;
        margin-top: 0.2rem;
        font-weight: 500;
        a {
            text-decoration: underline;
            cursor: pointer;
        }
        @media @snapeak_mobile {
            font-size: 0.28rem;
        }
    }
    .nav-wrapper{
        padding: 0 15px;
        margin-top: 32px;
    }

    .bottom-button-container {
        left: 0;
        .bottom-button-container();
    }

    @media @snapeak_mobile{

        /deep/ .reo-checkbox.small{
            width: 16px;
            height: 16px
        }

        /deep/ .reo-checkbox.small .check-icon{
            font-size: 13px;
        }

    }

    .reo-dialog.selectProduct{
        .select-product-dialog();
    }
    .dialogActivated .bottom-button-container{
        width: calc(100% - var(--scrollbar-width));
    }

    .refund-note{
        margin: 16px 0;
        font-size: 16px;
        .mobile({
            font-size: 14px;
        })
    }

    .refund-note__details--standalone{
        margin-left: 0;
    }

    .country-text{
        font-size: 16px;
        color: @orange_4;
        padding-top: 8px;
        .mobile({
            font-size: .36rem;
        });
    }
</style>
